import { forwardRef, useCallback, useMemo } from 'react';

import { InputIcon } from '@perpay-web/components/base//InputIcon/InputIcon';
import { InputError } from '@perpay-web/components/base/InputError/InputError';
import { InputLabel } from '@perpay-web/components/base/InputLabel/InputLabel';
import { InputPrefix } from '@perpay-web/components/base/InputPrefix/InputPrefix';
import { useEndToEndTestId } from '@perpay-web/hooks/useEndToEndTestId';
import { firstItemOf } from '@perpay-web/utils/arrayUtils';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from '@perpay-web/design/styles/inputs.scss';

export const SelectInput = forwardRef((props, ref) => {
    const {
        value = undefined,
        options = [],
        includeEmptyOption = false,
        name,
        className = '',
        label,
        touched = false,
        errors = null,
        loading = false,
        prefix,
        ...rest
    } = props;

    const endToEndTestIDParams = useEndToEndTestId();

    const isError = useMemo(
        () => firstItemOf(errors) && touched,
        [errors, touched],
    );

    const renderOptions = useCallback(
        () =>
            options && (
                <>
                    {includeEmptyOption ? (
                        <option disabled aria-label='Empty' value={null} />
                    ) : undefined}
                    {options.map((optionItem) => (
                        <option value={optionItem.value} key={optionItem.value}>
                            {optionItem.label}
                        </option>
                    ))}
                </>
            ),
        [options, includeEmptyOption],
    );

    return (
        <div
            className={getClassName(
                styles['text-field-row'],
                isError && styles['text-field-row--error'],
                className,
            )}
        >
            <div className={getClassName(styles['text-field-container'])}>
                <select
                    ref={ref}
                    value={value}
                    name={name}
                    autoComplete='off'
                    data-lpignore='true'
                    {...endToEndTestIDParams}
                    {...rest}
                    className={getClassName(
                        styles['text-field'],
                        value && styles['text-field--filled'],
                        label && styles['text-field--labeled'],
                        styles['text-field--icon'],
                    )}
                >
                    {renderOptions()}
                </select>
                <InputLabel label={label} />
                <InputPrefix prefix={prefix} />
                <InputIcon type='select' loading={loading} />
            </div>
            {isError ? <InputError name={name} error={errors[0]} /> : null}
        </div>
    );
});
